import { LitElement, html, css, PropertyValues } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import type { Character, Desk, AgentActivity, SubagentSpawn, InteractiveObject, IntegrationType } from "./types/index.js";
import {
  DEFAULT_COLS,
  DEFAULT_ROWS,
  ZOOM_DEFAULT,
  ZOOM_MIN,
  ZOOM_MAX,
  MEETING_CHANCE_PER_IDLE,
  MEETING_MIN_AGENTS,
  getMeetingPoints,
  INTERACTIVE_SUPPLIES,
  BAND_MEMBERS,
  type BandMemberConfig,
} from "./constants.js";
import {
  createCharacter,
  createUserAvatar,
  updateCharacter,
  updateOccupiedSleepSpots,
  setCharacterActive,
  showPermissionBubble,
  showWaitingBubble,
  showWarningBubble,
  showSetupBubble,
  showSpeechBubble,
  showSuccessBubble,
  clearBubble,
  startDespawn,
  startMeeting,
  startCollaboration,
  startHandoffAnimation,
  updateHandoffAnimation,
  isAvailableForMeeting,
  startDiscussion,
  updateDiscussion,
  stopDiscussion,
  setWorkingTogether,
  clearWorkingTogether,
  setUserFocus,
  moveUserToAgent,
  returnUserToDesk,
  moveAgentToTask,
} from "./engine/character.js";
import type { TaskType } from "./constants.js";
import { renderFrame } from "./engine/renderer.js";
import { preloadFurnitureImages } from "./furniture/loader.js";
import { getDefaultLayout } from "./layout.js";
import { clearCharacterSpriteCache } from "./sprites/data.js";
import { loadCharacterSprites } from "./sprites/loader.js";
import { loadFloorTiles, loadWallTiles, setFloorSprites, setWallSprites } from "./tiles/index.js";
import { Direction, TILE_SIZE } from "./types/index.js";

export interface AgentConfig {
  id: string;
  name: string;
  palette?: number;
  isSubagent?: boolean;
  parentAgentId?: string;
  isUserAvatar?: boolean;
}

export interface UserAvatarConfig {
  id: string;
  name: string;
  enabled: boolean;
}

export interface IntegrationStatus {
  type: "computer" | "phone" | "email" | "skills" | "slack" | "discord";
  connected: boolean;
  label: string;
  icon: string;
}

@customElement("pixel-office")
export class PixelOffice extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      background: #1a1a2e;
      border-radius: 8px;
      overflow: hidden;
    }
    
    .office-container {
      width: 100%;
      height: 100%;
      position: relative;
    }
    
    canvas {
      width: 100%;
      height: 100%;
      image-rendering: pixelated;
      image-rendering: crisp-edges;
      cursor: default;
    }
    
    canvas.hovering-agent {
      cursor: pointer;
    }
    
    .office-header {
      position: absolute;
      top: 8px;
      left: 12px;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.6);
    }
    
    .office-title {
      font-weight: 600;
      color: rgba(255, 255, 255, 0.8);
    }
    
    .agent-count {
      background: rgba(255, 255, 255, 0.1);
      padding: 2px 6px;
      border-radius: 4px;
    }
    
    .add-agent-btn {
      background: rgba(100, 200, 100, 0.2);
      border: 1px solid rgba(100, 200, 100, 0.4);
      color: rgba(100, 200, 100, 0.9);
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    
    .add-agent-btn:hover {
      background: rgba(100, 200, 100, 0.3);
      border-color: rgba(100, 200, 100, 0.6);
    }
    
    .zoom-controls {
      position: absolute;
      bottom: 8px;
      right: 12px;
      display: flex;
      gap: 4px;
    }
    
    .zoom-btn {
      width: 24px;
      height: 24px;
      border: none;
      background: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.7);
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      display: grid;
      place-items: center;
    }
    
    .zoom-btn:hover {
      background: rgba(255, 255, 255, 0.2);
    }
    
    .integrations-panel {
      position: absolute;
      top: 8px;
      right: 12px;
      display: flex;
      gap: 6px;
      padding: 4px 8px;
      background: rgba(0, 0, 0, 0.4);
      border-radius: 6px;
    }
    
    .integration-icon {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      border-radius: 4px;
      position: relative;
      cursor: help;
      transition: all 0.15s ease;
    }
    
    .integration-icon.connected {
      background: rgba(74, 222, 128, 0.2);
    }
    
    .integration-icon.disconnected {
      background: rgba(100, 100, 100, 0.3);
      opacity: 0.5;
      filter: grayscale(100%);
    }
    
    .integration-icon.disconnected::after {
      content: "!";
      position: absolute;
      top: -4px;
      right: -4px;
      width: 12px;
      height: 12px;
      background: #ef4444;
      border-radius: 50%;
      font-size: 9px;
      font-weight: bold;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .integration-icon:hover {
      transform: scale(1.1);
    }
    
    .integration-tooltip {
      position: absolute;
      bottom: -28px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 10px;
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.15s ease;
    }
    
    .integration-icon:hover .integration-tooltip {
      opacity: 1;
    }
  `;

  @property({ type: Array }) agents: AgentConfig[] = [];
  @property({ type: Number }) cols = DEFAULT_COLS;
  @property({ type: Number }) rows = DEFAULT_ROWS;
  @property({ type: Array }) integrations: IntegrationStatus[] = [];
  @property({ type: Object }) userAvatar: UserAvatarConfig | null = null;
  /** When true, spawns all 13 band members as background characters that sleep until needed */
  @property({ type: Boolean }) showBandMembers = false;

  @state() private _zoom = ZOOM_DEFAULT;
  @state() private _panX = 0;
  @state() private _panY = 0;
  @state() private _hoveredObjectId: string | null = null;

  @query("canvas") private _canvas!: HTMLCanvasElement;

  // Interactive office supplies - built from INTERACTIVE_SUPPLIES config
  private _interactiveObjects: InteractiveObject[] = [];

  private _ctx: CanvasRenderingContext2D | null = null;
  private _characters = new Map<string, Character>();
  private _desks: Desk[] = [];
  private _animationId: number | null = null;
  private _lastTime = 0;
  private _stopped = false;
  private _resizeObserver: ResizeObserver | null = null;
  private _meetingCheckTimer = 0;
  private _activeMeetingId: string | null = null;
  private _meetingParticipants: Set<string> = new Set();
  private _userAvatarId: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this._resizeObserver = new ResizeObserver(() => this._handleResize());
    this._resizeObserver.observe(this);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stopped = true;
    if (this._animationId !== null) {
      cancelAnimationFrame(this._animationId);
      this._animationId = null;
    }
    this._resizeObserver?.disconnect();
  }

  override firstUpdated(): void {
    try {
      this._ctx =
        this._canvas?.getContext("2d", {
          alpha: false, // Opaque background for better performance
          desynchronized: false, // Disable async - may cause GPU issues
          willReadFrequently: false, // We don't read pixels back
        }) || null;

      if (!this._ctx) {
        console.error("[PixelOffice] Failed to get 2D canvas context");
        return;
      }

      this._handleResize();
      this._startGameLoop();

      // Load PNG character sprites (with fallback to templates)
      loadCharacterSprites()
        .then((loaded) => {
          if (loaded.length > 0) {
            clearCharacterSpriteCache();
          }
        })
        .catch((err) => {
          console.warn("[PixelOffice] Failed to load character sprites:", err);
        });

      // Load floor and wall tiles
      loadFloorTiles()
        .then((sprites) => {
          if (sprites.length > 0) {
            setFloorSprites(sprites);
            console.log(`[PixelOffice] Loaded ${sprites.length} floor tile patterns`);
          }
        })
        .catch((err) => {
          console.warn("[PixelOffice] Failed to load floor tiles:", err);
        });

      loadWallTiles()
        .then((sprites) => {
          if (sprites.length > 0) {
            setWallSprites(sprites);
            console.log(`[PixelOffice] Loaded ${sprites.length} wall tile sprites`);
          }
        })
        .catch((err) => {
          console.warn("[PixelOffice] Failed to load wall tiles:", err);
        });

      // Preload furniture images
      const layout = getDefaultLayout();
      preloadFurnitureImages(layout)
        .then(() => {
          console.log("[PixelOffice] Furniture images loaded");
        })
        .catch((err) => {
          console.warn("[PixelOffice] Failed to load furniture images:", err);
        });

      // Initialize interactive objects
      this._syncInteractiveObjects();
    } catch (err) {
      console.error("[PixelOffice] Initialization error:", err);
    }
  }

  override updated(changedProps: PropertyValues): void {
    if (changedProps.has("agents") || changedProps.has("showBandMembers")) {
      this._syncAgents();
      if (this.showBandMembers) {
        this._syncBandMembers();
      }
    }
    if (changedProps.has("userAvatar")) {
      this._syncUserAvatar();
    }
    if (changedProps.has("integrations")) {
      this._syncInteractiveObjects();
    }
  }

  private _syncInteractiveObjects(): void {
    // Build interactive objects from config, marking configured status based on integrations
    this._interactiveObjects = INTERACTIVE_SUPPLIES.map((supply) => {
      // Map supply type to integration type for checking configured status
      const integrationTypeMap: Record<string, string[]> = {
        phone: ["phone"],
        computer: ["computer"],
        notepad: ["skills"], // Notes could map to skills/tools
        calendar: ["skills"], // Calendar could map to skills
        email: ["email"],
      };

      const matchingTypes = integrationTypeMap[supply.type] || [];
      const isConfigured = this.integrations.some(
        (int) => matchingTypes.includes(int.type) && int.connected,
      );

      return {
        id: `supply-${supply.type}`,
        type: supply.type as IntegrationType,
        col: supply.col,
        row: supply.row,
        width: supply.width,
        height: supply.height,
        configured: isConfigured,
        tooltip: isConfigured ? `${supply.type} connected` : supply.tooltip,
      };
    });
  }

  private _syncUserAvatar(): void {
    if (this.userAvatar?.enabled) {
      // Create user avatar if not exists
      if (!this._userAvatarId || !this._characters.has(this._userAvatarId)) {
        const avatar = createUserAvatar(this.userAvatar.id, this.userAvatar.name);
        this._characters.set(avatar.id, avatar);
        this._userAvatarId = avatar.id;
      } else {
        // Update name if changed
        const ch = this._characters.get(this._userAvatarId);
        if (ch) {
          ch.name = this.userAvatar.name;
        }
      }
    } else if (this._userAvatarId) {
      // Remove user avatar
      const ch = this._characters.get(this._userAvatarId);
      if (ch) {
        startDespawn(ch);
      }
      this._userAvatarId = null;
    }
  }

  private _handleResize(): void {
    if (!this._canvas) return;
    const rect = this.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return; // Skip if not visible

    // Size canvas to device pixels (no ctx.scale - render directly in device pixels)
    const dpr = window.devicePixelRatio || 1;
    this._canvas.width = Math.round(rect.width * dpr);
    this._canvas.height = Math.round(rect.height * dpr);
    this._canvas.style.width = `${rect.width}px`;
    this._canvas.style.height = `${rect.height}px`;
    // No ctx.scale(dpr) - we render directly in device pixels like pixel-agents
  }

  private _syncAgents(): void {
    // Start despawn for characters whose agents no longer exist (but aren't already despawning)
    // Skip band members - they persist independently of the agents array
    for (const [id, ch] of this._characters) {
      if (ch.isBandMember) continue; // Don't despawn band members
      if (ch.isUserAvatar) continue; // Don't despawn user avatar
      if (!this.agents.find((a) => a.id === id) && ch.matrixEffect !== "despawn") {
        startDespawn(ch);
      }
    }

    // Office layout based on actual walkable tiles:
    // - Meeting room: rows 2-7, cols 3-8 (floor type 2)
    // - Main work area: rows 11-15, cols 2-11 (floor type 2)
    // - Lounge: rows 11-15, cols 12-19 (floor type 6)
    // - Kitchen: rows 16-19, cols 12-19 (floor type 1)

    this._desks = [];
    let deskIndex = 0;

    // Desk positions - first position is CEO office for main agent (Ajax)
    // Characters sit at these positions (behind desk furniture which is one row south)
    const deskPositions = [
      { col: 22, row: 3 }, // CEO office - Ajax sits here, desk furniture at row 4
      { col: 3, row: 10 }, // Work area desk 1
      { col: 9, row: 10 }, // Work area desk 2
      { col: 15, row: 10 }, // Work area desk 3
      { col: 21, row: 10 }, // Work area desk 4
      { col: 3, row: 13 }, // Work area desk 5
      { col: 9, row: 13 }, // Work area desk 6
    ];

    for (const agent of this.agents) {
      // Skip subagents in desk assignment - they don't get their own desk
      if (agent.isSubagent) continue;

      const deskPos = deskPositions[deskIndex % deskPositions.length];
      if (!deskPos) {
        deskIndex++;
        continue;
      }

      if (!this._characters.has(agent.id)) {
        // Create desk
        const desk: Desk = {
          id: `desk-${agent.id}`,
          col: deskPos.col,
          row: deskPos.row,
          assignedAgentId: agent.id,
          facingDir: Direction.DOWN,
        };
        this._desks.push(desk);

        // Create character at desk position (integer coords for proper pathfinding)
        const ch = createCharacter(
          agent.id,
          agent.name,
          agent.palette ?? deskIndex % 12,
          deskPos.col, // Use integer position
          deskPos.row, // Use integer position
          Direction.DOWN,
          true, // withSpawnEffect
        );
        this._characters.set(agent.id, ch);
      } else {
        // Update existing character's name if changed
        const ch = this._characters.get(agent.id)!;
        ch.name = agent.name;
        // Add desk for existing agent
        this._desks.push({
          id: `desk-${agent.id}`,
          col: deskPos.col,
          row: deskPos.row,
          assignedAgentId: agent.id,
          facingDir: Direction.DOWN,
        });
      }
      deskIndex++;
    }

    // Handle subagents - spawn near their parent
    for (const agent of this.agents) {
      if (!agent.isSubagent || !agent.parentAgentId) continue;

      if (!this._characters.has(agent.id)) {
        const parent = this._characters.get(agent.parentAgentId);
        if (!parent) continue;

        // Spawn subagent near parent (offset slightly)
        const offsetX = (Math.random() - 0.5) * 2;
        const offsetY = (Math.random() - 0.5) * 2;
        const ch = createCharacter(
          agent.id,
          agent.name,
          agent.palette ?? (parent.palette + 1) % 12,
          parent.tileCol + offsetX,
          parent.tileRow + offsetY,
          Direction.DOWN,
          true,
        );
        ch.isSubagent = true;
        ch.parentAgentId = agent.parentAgentId;
        this._characters.set(agent.id, ch);
      }
    }

    // Auto-select the main agent (first non-subagent) if nothing is selected
    const anySelected = Array.from(this._characters.values()).some((ch) => ch.selected);
    if (!anySelected && this.agents.length > 0) {
      const mainAgent = this.agents.find((a) => !a.isSubagent);
      if (mainAgent) {
        this.selectAgent(mainAgent.id);
        // Dispatch event so parent component knows agent was auto-selected
        this.dispatchEvent(
          new CustomEvent("agent-selected", {
            detail: { agentId: mainAgent.id, agentName: mainAgent.name, autoSelected: true },
            bubbles: true,
            composed: true,
          }),
        );
      }
    }
  }

  /**
   * Sync band members as background characters.
   * Band members are marked with isBandMember=true and prefer to sleep when idle.
   * They don't get desks - they wander and sleep until activated by delegation.
   */
  private _syncBandMembers(): void {
    const activeAgentIds = new Set(this.agents.map((a) => a.id));

    for (const member of BAND_MEMBERS) {
      // Skip if this band member is already an active agent (avoid duplicates)
      if (activeAgentIds.has(member.id)) continue;
      // Skip if character already exists
      if (this._characters.has(member.id)) continue;

      // Create band member character at their home desk position
      // They'll immediately start wandering toward sleep spots
      const ch = createCharacter(
        member.id,
        member.name,
        member.palette,
        member.homeDesk.col,
        member.homeDesk.row,
        Direction.DOWN,
        true, // withSpawnEffect
      );

      // Mark as band member (prefers sleep when idle)
      ch.isBandMember = true;
      ch.homeDesk = { col: member.homeDesk.col, row: member.homeDesk.row };

      this._characters.set(member.id, ch);
    }
  }

  private _startGameLoop(): void {
    this._stopped = false;
    this._lastTime = performance.now();

    const loop = (now: number) => {
      if (this._stopped) return;

      const dt = Math.min((now - this._lastTime) / 1000, 0.1); // Cap dt to 100ms
      this._lastTime = now;

      // Track occupied sleep spots before updating characters (prevents stacking)
      updateOccupiedSleepSpots(Array.from(this._characters.values()));

      // Update all characters
      const toRemove: string[] = [];
      for (const [id, ch] of this._characters) {
        const shouldRemove = updateCharacter(ch, dt);
        if (shouldRemove) {
          toRemove.push(id);
        }
      }
      // Remove despawned characters
      for (const id of toRemove) {
        this._characters.delete(id);
        // Remove from meeting if they were participating
        this._meetingParticipants.delete(id);
      }

      // Update handoff animations
      for (const ch of this._characters.values()) {
        if (ch.handoffProgress !== undefined && ch.handoffProgress !== null) {
          updateHandoffAnimation(ch, dt);
        }
      }

      // Update discussion animations
      for (const ch of this._characters.values()) {
        if (ch.isDiscussing) {
          updateDiscussion(ch, dt);
        }
      }

      // Check for team meeting opportunities
      this._checkTeamMeetings(dt);

      // Render frame
      if (this._ctx && this._canvas) {
        this._ctx.imageSmoothingEnabled = false;
        renderFrame(
          this._ctx,
          this._canvas.width,
          this._canvas.height,
          this.cols,
          this.rows,
          this._desks,
          Array.from(this._characters.values()),
          this._zoom,
          this._panX,
          this._panY,
          this._interactiveObjects,
          this._hoveredObjectId,
        );
      }

      this._animationId = requestAnimationFrame(loop);
    };

    this._animationId = requestAnimationFrame(loop);
  }

  // Public API: update agent activity
  updateActivity(activity: AgentActivity): void {
    const ch = this._characters.get(activity.agentId);
    if (!ch) return;

    const isActive = activity.type !== "idle";
    setCharacterActive(ch, isActive, activity.tool);

    // Set status text for display (custom status or null for tool-based default)
    ch.statusText = isActive ? activity.status || null : null;
  }

  // Public API: set multiple activities at once
  updateActivities(activities: AgentActivity[]): void {
    for (const activity of activities) {
      this.updateActivity(activity);
    }
  }

  // Public API: spawn a subagent (Task tool)
  spawnSubagent(spawn: SubagentSpawn): void {
    const parentAgent = this.agents.find((a) => a.id === spawn.parentAgentId);
    if (!parentAgent) return;

    const subagentId = `${spawn.parentAgentId}-${spawn.toolId}`;
    const subagentConfig: AgentConfig = {
      id: subagentId,
      name: spawn.name ?? spawn.toolId,
      isSubagent: true,
      parentAgentId: spawn.parentAgentId,
    };

    // Add to agents list (will trigger _syncAgents via updated())
    this.agents = [...this.agents, subagentConfig];
  }

  // Public API: remove a subagent (Task tool completed)
  despawnSubagent(subagentId: string): void {
    const ch = this._characters.get(subagentId);
    if (ch) {
      startDespawn(ch);
    }
    // Remove from agents list
    this.agents = this.agents.filter((a) => a.id !== subagentId);
  }

  // Public API: show permission bubble (waiting for user input)
  showPermission(agentId: string): void {
    const ch = this._characters.get(agentId);
    if (ch) {
      showPermissionBubble(ch);
    }
  }

  // Public API: clear permission and show waiting bubble (permission granted)
  permissionGranted(agentId: string): void {
    const ch = this._characters.get(agentId);
    if (ch) {
      showWaitingBubble(ch);
    }
  }

  // Public API: clear any bubble
  clearBubble(agentId: string): void {
    const ch = this._characters.get(agentId);
    if (ch) {
      clearBubble(ch);
    }
  }

  // Public API: show warning bubble with attention count
  showWarning(agentId: string, attentionCount: number): void {
    const ch = this._characters.get(agentId);
    if (ch) {
      showWarningBubble(ch, attentionCount);
    }
  }

  // Public API: show setup bubble for onboarding items
  showSetup(agentId: string): void {
    const ch = this._characters.get(agentId);
    if (ch) {
      showSetupBubble(ch);
    }
  }

  // Public API: update attention state for multiple agents
  updateAttentionStates(states: Array<{ agentId: string; attentionCount: number }>): void {
    for (const { agentId, attentionCount } of states) {
      const ch = this._characters.get(agentId);
      if (ch) {
        if (attentionCount > 0) {
          showWarningBubble(ch, attentionCount);
        } else {
          // Only clear if it was a warning bubble
          if (ch.bubbleType === "warning") {
            clearBubble(ch);
          }
        }
      }
    }
  }

  // Public API: update real-time metrics for an agent's overlay display
  updateAgentMetrics(
    agentId: string,
    metrics: { tokensPerSec?: number; costCents?: number; latencyMs?: number },
  ): void {
    const ch = this._characters.get(agentId);
    if (!ch) return;

    ch.metricsOverlay = {
      ...ch.metricsOverlay,
      ...metrics,
      lastUpdated: Date.now(),
    };
  }

  // Public API: select an agent
  selectAgent(agentId: string): void {
    for (const ch of this._characters.values()) {
      ch.selected = ch.id === agentId;
    }
  }

  // Public API: hover an agent
  hoverAgent(agentId: string | null): void {
    for (const ch of this._characters.values()) {
      ch.hovered = ch.id === agentId;
    }
  }

  // Public API: trigger a team meeting between multiple agents
  // Set force=true for explicitly triggered meetings (e.g., persona spawns) that should override availability checks
  triggerMeeting(agentIds: string[], force = false): boolean {
    if (agentIds.length < MEETING_MIN_AGENTS) return false;

    const meetingPoints = getMeetingPoints();
    if (meetingPoints.length === 0) return false;

    const participants: Character[] = [];
    for (const id of agentIds) {
      const ch = this._characters.get(id);
      if (ch && (force || isAvailableForMeeting(ch))) {
        participants.push(ch);
      }
    }

    if (participants.length < MEETING_MIN_AGENTS) return false;

    // Assign seats and start meeting
    const meetingId = `meeting-${Date.now()}`;
    this._activeMeetingId = meetingId;
    this._meetingParticipants.clear();

    const participantIds = participants.map((p) => p.id);

    for (let i = 0; i < participants.length && i < meetingPoints.length; i++) {
      const ch = participants[i];
      const seatIndex = meetingPoints[i].meetingSeatIndex ?? i;
      if (
        startMeeting(
          ch,
          seatIndex,
          participantIds.filter((id) => id !== ch.id),
          i === 0,
        )
      ) {
        this._meetingParticipants.add(ch.id);
      }
    }

    // Dispatch meeting started event
    this.dispatchEvent(
      new CustomEvent("meeting-started", {
        detail: { meetingId, participants: Array.from(this._meetingParticipants) },
        bubbles: true,
        composed: true,
      }),
    );

    return this._meetingParticipants.size >= MEETING_MIN_AGENTS;
  }

  // Public API: explicitly end the current meeting (e.g., when all personas complete)
  endMeeting(): void {
    if (!this._activeMeetingId) return;

    // Send all meeting participants back to their home desks
    for (const id of this._meetingParticipants) {
      const ch = this._characters.get(id);
      if (ch && ch.state === "meeting") {
        // Reset to idle at home desk
        ch.state = "idle";
        if (ch.homeDesk) {
          ch.tileCol = ch.homeDesk.col;
          ch.tileRow = ch.homeDesk.row;
        }
      }
    }

    this._meetingParticipants.clear();
    const meetingId = this._activeMeetingId;
    this._activeMeetingId = null;

    this.dispatchEvent(
      new CustomEvent("meeting-ended", {
        detail: { meetingId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  // Public API: trigger collaboration between two agents
  triggerCollaboration(initiatorId: string, targetId: string): boolean {
    const initiator = this._characters.get(initiatorId);
    const target = this._characters.get(targetId);

    if (!initiator || !target) return false;
    if (!isAvailableForMeeting(initiator)) return false;

    const success = startCollaboration(initiator, targetId, target.tileCol, target.tileRow);

    if (success) {
      target.collaboratingWith = initiatorId;

      // Dispatch collaboration started event
      this.dispatchEvent(
        new CustomEvent("collaboration-started", {
          detail: { initiatorId, targetId },
          bubbles: true,
          composed: true,
        }),
      );
    }

    return success;
  }

  // Public API: trigger a handoff animation (visual indicator of work passing)
  triggerHandoff(senderId: string, receiverId: string): void {
    const sender = this._characters.get(senderId);
    const receiver = this._characters.get(receiverId);

    if (sender && receiver) {
      startHandoffAnimation(sender, receiverId);

      // Dispatch handoff event
      this.dispatchEvent(
        new CustomEvent("handoff-started", {
          detail: { senderId, receiverId },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  // Public API: check if any meeting is currently active
  isMeetingActive(): boolean {
    return this._meetingParticipants.size >= MEETING_MIN_AGENTS;
  }

  // Public API: get agents currently in meeting
  getMeetingParticipants(): string[] {
    return Array.from(this._meetingParticipants);
  }

  // Public API: show speech bubble from user avatar (when user sends message)
  userSpeaks(message?: string): void {
    if (!this._userAvatarId) return;
    const ch = this._characters.get(this._userAvatarId);
    if (ch) {
      showSpeechBubble(ch);
      // Dispatch event for external handling
      this.dispatchEvent(
        new CustomEvent("user-speaks", {
          detail: { message },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  // Public API: focus user avatar on a specific agent
  focusOnAgent(agentId: string): void {
    if (!this._userAvatarId) return;
    const userCh = this._characters.get(this._userAvatarId);
    const targetCh = this._characters.get(agentId);
    if (userCh && targetCh) {
      setUserFocus(userCh, agentId);
    }
  }

  // Public API: clear user focus
  clearUserFocus(): void {
    if (!this._userAvatarId) return;
    const ch = this._characters.get(this._userAvatarId);
    if (ch) {
      setUserFocus(ch, null);
    }
  }

  // Public API: move user avatar to observe an agent
  moveUserToObserve(agentId: string): boolean {
    if (!this._userAvatarId) return false;
    const userCh = this._characters.get(this._userAvatarId);
    const targetCh = this._characters.get(agentId);
    if (!userCh || !targetCh) return false;

    const success = moveUserToAgent(userCh, targetCh.tileCol, targetCh.tileRow);
    if (success) {
      setUserFocus(userCh, agentId);
    }
    return success;
  }

  // Public API: return user avatar to CEO desk
  returnUserToDesk(): boolean {
    if (!this._userAvatarId) return false;
    const ch = this._characters.get(this._userAvatarId);
    if (!ch) return false;
    setUserFocus(ch, null);
    return returnUserToDesk(ch);
  }

  // Public API: move agent to task-specific desk based on detected task type
  moveAgentToTaskDesk(agentId: string, taskType: TaskType): boolean {
    const ch = this._characters.get(agentId);
    if (!ch) return false;

    const success = moveAgentToTask(ch, taskType);
    if (success) {
      // Dispatch event for task movement
      this.dispatchEvent(
        new CustomEvent("agent-task-started", {
          detail: { agentId, taskType },
          bubbles: true,
          composed: true,
        }),
      );
    }
    return success;
  }

  // Public API: set agents as working together (same task)
  setAgentsWorkingTogether(agentIds: string[]): void {
    const characters = agentIds
      .map((id) => this._characters.get(id))
      .filter((ch): ch is NonNullable<typeof ch> => ch !== undefined);

    if (characters.length >= 2) {
      setWorkingTogether(characters);

      this.dispatchEvent(
        new CustomEvent("working-together-started", {
          detail: { agentIds },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  // Public API: clear working together state for agents
  clearAgentsWorkingTogether(agentIds: string[]): void {
    for (const id of agentIds) {
      const ch = this._characters.get(id);
      if (ch) {
        clearWorkingTogether(ch);
      }
    }
  }

  // Public API: start a discussion between agents
  startAgentDiscussion(agentIds: string[]): void {
    const characters = agentIds
      .map((id) => this._characters.get(id))
      .filter((ch): ch is NonNullable<typeof ch> => ch !== undefined);

    if (characters.length >= 2) {
      startDiscussion(characters);

      this.dispatchEvent(
        new CustomEvent("discussion-started", {
          detail: { agentIds },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  // Public API: stop discussion for agents
  stopAgentDiscussion(agentIds: string[]): void {
    for (const id of agentIds) {
      const ch = this._characters.get(id);
      if (ch) {
        stopDiscussion(ch);
      }
    }
  }

  // Public API: trigger orchestrator handoff to subagent (with visual walk + handoff)
  triggerOrchestratorDelegation(orchestratorId: string, subagentId: string): void {
    const orchestrator = this._characters.get(orchestratorId);
    const subagent = this._characters.get(subagentId);

    if (!orchestrator || !subagent) return;

    // Start handoff animation
    startHandoffAnimation(orchestrator, subagentId);

    // Show speech bubble on orchestrator
    showSpeechBubble(orchestrator, 2.0);

    this.dispatchEvent(
      new CustomEvent("orchestrator-delegation", {
        detail: { orchestratorId, subagentId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  // Public API: wake up a sleeping band member and delegate work to them
  wakeBandMember(bandMemberId: string): boolean {
    const ch = this._characters.get(bandMemberId);
    if (!ch) return false;

    // Wake them up by setting active
    ch.isActive = true;
    // Clear any sleep bubble
    if (ch.bubbleType === "sleep") {
      ch.bubbleType = "message"; // Show notification bubble
      ch.bubbleTimer = 3.0;
    }

    this.dispatchEvent(
      new CustomEvent("band-member-woke", {
        detail: { bandMemberId },
        bubbles: true,
        composed: true,
      }),
    );

    return true;
  }

  // Public API: delegate to a band member (wake + handoff animation)
  delegateToBandMember(fromAgentId: string, toBandMemberId: string): boolean {
    const fromAgent = this._characters.get(fromAgentId);
    const toBandMember = this._characters.get(toBandMemberId);

    if (!fromAgent || !toBandMember) return false;

    // Wake up the band member
    this.wakeBandMember(toBandMemberId);

    // Show handoff animation from delegating agent
    startHandoffAnimation(fromAgent, toBandMemberId);

    // Show speech bubble on both
    showSpeechBubble(fromAgent, 2.0);

    this.dispatchEvent(
      new CustomEvent("band-delegation", {
        detail: { fromAgentId, toBandMemberId },
        bubbles: true,
        composed: true,
      }),
    );

    return true;
  }

  // Public API: put a band member back to sleep (task completed successfully)
  sleepBandMember(bandMemberId: string): void {
    const ch = this._characters.get(bandMemberId);
    if (!ch || !ch.isBandMember) return;

    // Show success bubble (green checkmark) briefly before sleeping
    showSuccessBubble(ch, 2.0);

    // Mark as inactive after showing success - they'll naturally wander to sleep spot
    setTimeout(() => {
      if (ch.bubbleType === "success") {
        ch.bubbleType = null;
        ch.bubbleTimer = 0;
      }
      ch.isActive = false;
    }, 2000);

    this.dispatchEvent(
      new CustomEvent("band-member-completed", {
        detail: { bandMemberId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  // Public API: show error state on a band member (task failed)
  errorBandMember(bandMemberId: string): void {
    const ch = this._characters.get(bandMemberId);
    if (!ch || !ch.isBandMember) return;

    // Show warning bubble to indicate error
    ch.bubbleType = "warning";
    ch.bubbleTimer = 5.0; // Show error state for 5 seconds
    ch.attentionCount = 1; // Red exclamation mark

    // After showing error, put back to sleep
    setTimeout(() => {
      this.sleepBandMember(bandMemberId);
    }, 5000);

    this.dispatchEvent(
      new CustomEvent("band-member-error", {
        detail: { bandMemberId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  // Public API: get all band members and their states
  getBandMemberStates(): Array<{ id: string; name: string; state: string; isActive: boolean }> {
    const states: Array<{ id: string; name: string; state: string; isActive: boolean }> = [];
    for (const ch of this._characters.values()) {
      if (ch.isBandMember) {
        states.push({
          id: ch.id,
          name: ch.name,
          state: ch.state,
          isActive: ch.isActive,
        });
      }
    }
    return states;
  }

  // Public API: trigger a complex task meeting (multiple agents gather)
  triggerComplexTaskMeeting(agentIds: string[], taskDescription?: string): boolean {
    if (agentIds.length < 2) return false;

    const participants = agentIds
      .map((id) => this._characters.get(id))
      .filter((ch): ch is NonNullable<typeof ch> => ch !== undefined && !ch.isUserAvatar);

    if (participants.length < 2) return false;

    // Start discussion animation for all participants
    startDiscussion(participants);

    // Trigger meeting (walk to meeting table)
    const success = this.triggerMeeting(participants.map((p) => p.id));

    if (success) {
      this.dispatchEvent(
        new CustomEvent("complex-task-meeting", {
          detail: { agentIds, taskDescription },
          bubbles: true,
          composed: true,
        }),
      );
    }

    return success;
  }

  // Check for team meeting opportunities (called from game loop)
  private _checkTeamMeetings(dt: number): void {
    // Only check periodically
    this._meetingCheckTimer += dt;
    if (this._meetingCheckTimer < 5.0) return; // Check every 5 seconds
    this._meetingCheckTimer = 0;

    // Need at least MEETING_MIN_AGENTS to have a meeting
    const nonSubagents = Array.from(this._characters.values()).filter((ch) => !ch.isSubagent);
    if (nonSubagents.length < MEETING_MIN_AGENTS) return;

    // If meeting is already active, don't start another
    if (this._meetingParticipants.size > 0) {
      // Check if meeting is still ongoing
      let meetingStillActive = false;
      for (const id of this._meetingParticipants) {
        const ch = this._characters.get(id);
        if (ch && (ch.state === "meeting" || ch.state === "wander")) {
          meetingStillActive = true;
          break;
        }
      }
      if (!meetingStillActive) {
        // Meeting ended
        this._meetingParticipants.clear();
        this._activeMeetingId = null;
        this.dispatchEvent(
          new CustomEvent("meeting-ended", {
            bubbles: true,
            composed: true,
          }),
        );
      }
      return;
    }

    // Find available agents for meeting (not active/working, not band members)
    // Band members don't randomly join meetings - they only participate when explicitly delegated to
    const availableAgents = nonSubagents.filter((ch) => isAvailableForMeeting(ch) && !ch.isBandMember);
    if (availableAgents.length < MEETING_MIN_AGENTS) return;

    // Random chance to start a meeting
    if (Math.random() > MEETING_CHANCE_PER_IDLE) return;

    // Pick 2-4 agents for the meeting
    const meetingSize = Math.min(
      availableAgents.length,
      Math.floor(Math.random() * 3) + 2, // 2-4 agents
    );

    // Shuffle and pick agents
    const shuffled = [...availableAgents].sort(() => Math.random() - 0.5);
    const meetingAgents = shuffled.slice(0, meetingSize);

    this.triggerMeeting(meetingAgents.map((ch) => ch.id));
  }

  private _zoomIn(): void {
    this._zoom = Math.min(this._zoom + 0.5, ZOOM_MAX);
  }

  private _zoomOut(): void {
    this._zoom = Math.max(this._zoom - 0.5, ZOOM_MIN);
  }

  private _getWorldCoords(clientX: number, clientY: number): { worldX: number; worldY: number } | null {
    if (!this._canvas) return null;

    const rect = this._canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    // Convert CSS coords to device pixels (matching canvas rendering)
    const cssX = clientX - rect.left;
    const cssY = clientY - rect.top;
    const deviceX = cssX * dpr;
    const deviceY = cssY * dpr;

    // Calculate offsets in device pixels (matching renderer)
    const canvasWidth = this._canvas.width;
    const canvasHeight = this._canvas.height;
    const layout = getDefaultLayout();
    const layoutCols = layout.cols;
    const layoutRows = layout.rows;
    const mapW = layoutCols * TILE_SIZE * this._zoom;
    const mapH = layoutRows * TILE_SIZE * this._zoom;
    const offsetX = Math.floor((canvasWidth - mapW) / 2) + Math.round(this._panX * dpr);
    const offsetY = Math.floor((canvasHeight - mapH) / 2) + Math.round(this._panY * dpr);

    // Convert device coords to world coords
    const worldX = (deviceX - offsetX) / this._zoom;
    const worldY = (deviceY - offsetY) / this._zoom;

    return { worldX, worldY };
  }

  private _getCharacterAtPosition(clientX: number, clientY: number): Character | null {
    const coords = this._getWorldCoords(clientX, clientY);
    if (!coords) return null;

    const { worldX, worldY } = coords;

    // Find character under cursor (in world/sprite pixel coordinates)
    for (const ch of this._characters.values()) {
      // Character sprite is 16x24 pixels, anchored at bottom-center
      const halfW = 8;
      const height = 24;

      if (
        worldX >= ch.x - halfW &&
        worldX <= ch.x + halfW &&
        worldY >= ch.y - height &&
        worldY <= ch.y
      ) {
        return ch;
      }
    }
    return null;
  }

  private _getInteractiveObjectAtPosition(clientX: number, clientY: number): InteractiveObject | null {
    const coords = this._getWorldCoords(clientX, clientY);
    if (!coords) return null;

    const { worldX, worldY } = coords;

    // Find interactive object under cursor (in tile coordinates)
    for (const obj of this._interactiveObjects) {
      const objX = obj.col * TILE_SIZE;
      const objY = obj.row * TILE_SIZE;

      if (
        worldX >= objX &&
        worldX <= objX + obj.width &&
        worldY >= objY &&
        worldY <= objY + obj.height
      ) {
        return obj;
      }
    }
    return null;
  }

  private _handleCanvasMouseMove(e: MouseEvent): void {
    const ch = this._getCharacterAtPosition(e.clientX, e.clientY);
    const obj = this._getInteractiveObjectAtPosition(e.clientX, e.clientY);

    // Update cursor style - prioritize characters, then unconfigured objects
    if (ch) {
      this._canvas?.classList.add("hovering-agent");
    } else if (obj && !obj.configured) {
      this._canvas?.classList.add("hovering-agent");
    } else {
      this._canvas?.classList.remove("hovering-agent");
    }

    // Update hover states
    this.hoverAgent(ch?.id ?? null);
    this._hoveredObjectId = obj?.id ?? null;
  }

  private _handleCanvasClick(e: MouseEvent): void {
    const ch = this._getCharacterAtPosition(e.clientX, e.clientY);
    const obj = this._getInteractiveObjectAtPosition(e.clientX, e.clientY);

    // Prioritize character clicks
    if (ch) {
      this.dispatchEvent(
        new CustomEvent("agent-selected", {
          detail: { agentId: ch.id, agentName: ch.name },
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }

    // Handle interactive object clicks (only unconfigured ones)
    if (obj && !obj.configured) {
      this.dispatchEvent(
        new CustomEvent("office-supply-clicked", {
          detail: { type: obj.type, objectId: obj.id, tooltip: obj.tooltip },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  override render() {
    return html`
      <div class="office-container">
        <canvas
          @click=${this._handleCanvasClick}
          @mousemove=${this._handleCanvasMouseMove}
          @mouseleave=${() => {
            this._canvas?.classList.remove("hovering-agent");
            this.hoverAgent(null);
            this._hoveredObjectId = null;
          }}
        ></canvas>
        <div class="office-header">
          <span class="office-title">Rax Office</span>
          <span class="agent-count">${this.agents.length} agent${this.agents.length !== 1 ? "s" : ""}</span>
          <button
            class="add-agent-btn"
            @click=${() => this.dispatchEvent(new CustomEvent("add-agent", { bubbles: true, composed: true }))}
          >+ Agent</button>
        </div>
        <div class="zoom-controls">
          <button class="zoom-btn" @click=${this._zoomOut}>-</button>
          <button class="zoom-btn" @click=${this._zoomIn}>+</button>
        </div>
        ${
          this.integrations.length > 0
            ? html`
          <div class="integrations-panel">
            ${this.integrations.map(
              (int) => html`
              <div
                class="integration-icon ${int.connected ? "connected" : "disconnected"}"
                @click=${() => this._handleIntegrationClick(int)}
              >
                ${int.icon}
                <span class="integration-tooltip">
                  ${int.label}: ${int.connected ? "Connected" : "Not connected"}
                </span>
              </div>
            `,
            )}
          </div>
        `
            : ""
        }
      </div>
    `;
  }

  private _handleIntegrationClick(integration: IntegrationStatus): void {
    if (!integration.connected) {
      this.dispatchEvent(
        new CustomEvent("setup-integration", {
          detail: { type: integration.type },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "pixel-office": PixelOffice;
  }
}
